import {
  InfiniteData,
  infiniteQueryOptions,
  queryOptions,
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

import { useNavigate } from 'react-router-dom';
import { CommonError } from 'shared/api/types';

import { queryClient } from 'shared/lib/react-query';

import { pathKeys } from 'shared/lib/react-router';
import { addToast } from 'shared/model/toast';
import {
  calendarsListGet,
  getCalendarById,
  createCalendarEvent,
  deleteCalendarEvent,
  updateCalendarEvent,
  createRegionalCalendar,
  updateRegionalCalendar,
  deleteRegionalCalendar,
} from './requests';
import {
  CalendarDTO,
  CountryWithCalendarsListRequest,
  CountryWithCalendarsList,
  GetCalendarByIdRequest,
  CalendarEvent,
  UpdateRegionalCalendarRequest,
  CreateRegionalCalendarRequest,
} from './types';

export const keys = {
  root: 'calendars',
  list: () => [keys.root, 'list'] as const,
  listWithFilters: (parameters: Omit<CountryWithCalendarsListRequest, 'page'>) =>
    [...keys.list(), 'listWithFilters', parameters] as const,
  item: (parameters: GetCalendarByIdRequest) => [keys.root, 'item', parameters] as const,
  createEvent: (id: string) => [keys.root, 'createEvent', id] as const,
  updateEvent: (id: string) => [keys.root, 'updateEvent', id] as const,
  deleteEvent: (id: string) => [keys.root, 'deleteEvent', id] as const,
  createRegionalCalendar: (id: string) => [keys.root, 'createRegionalCalendar', id] as const,
  updateRegionalCalendar: (id: string) => [keys.root, 'updateRegionalCalendar', id] as const,
  deleteRegionalCalendar: (id: string) => [keys.root, 'deleteRegionalCalendar', id] as const,
};

export const calendarsService = {
  queryKey: (parameters: Omit<CountryWithCalendarsListRequest, 'page'>) => keys.listWithFilters(parameters),

  getCache: (parameters: Omit<CountryWithCalendarsListRequest, 'page'>) =>
    queryClient.getQueryData<InfiniteData<CountryWithCalendarsList>>(calendarsService.queryKey(parameters)),

  queryOptions: (parameters: Omit<CountryWithCalendarsListRequest, 'page'>) => {
    const calendarsKey = calendarsService.queryKey(parameters);
    return infiniteQueryOptions({
      queryKey: calendarsKey,
      queryFn: async ({ pageParam, signal }) => {
        const data = await calendarsListGet({ ...parameters, page: pageParam.page }, { signal });
        return data;
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      initialPageParam: { page: 0 } as any,
      getNextPageParam: (lastPage) => {
        if (lastPage.page.totalPages <= lastPage.page.number + 1) {
          return null;
        }
        return {
          page: lastPage.page.number + 1,
        };
      },
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      initialData: () => calendarsService.getCache(parameters)!,
      initialDataUpdatedAt: () => queryClient.getQueryState(calendarsKey)?.dataUpdatedAt,
    });
  },

  prefetchQuery: async (filters: Omit<CountryWithCalendarsListRequest, 'page'>) => {
    queryClient.prefetchInfiniteQuery(calendarsService.queryOptions(filters));
  },
};

export const calendarService = {
  queryKey: (parameters: GetCalendarByIdRequest) => keys.item(parameters),
  getCache: (parameters: GetCalendarByIdRequest) =>
    queryClient.getQueryData<CalendarDTO>(calendarService.queryKey(parameters)),

  queryOptions: (parameters: GetCalendarByIdRequest, enabled = true) => {
    const calendarKey = calendarService.queryKey(parameters);
    return queryOptions({
      queryKey: calendarKey,
      queryFn: async ({ signal }) => {
        const calendar = await getCalendarById(parameters, { signal });
        return calendar;
      },
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      initialData: () => calendarService.getCache(parameters)!,
      initialDataUpdatedAt: () => queryClient.getQueryState(calendarKey)?.dataUpdatedAt,
      enabled,
    });
  },

  prefetchQuery: async (filters: GetCalendarByIdRequest) =>
    queryClient.prefetchQuery(calendarService.queryOptions(filters)),
};

export const useCreateCalendarEventMutation = (
  calendarId: string,
): UseMutationResult<CalendarEvent, CommonError, Omit<CalendarEvent, 'id'>> => {
  const qClient = useQueryClient();

  return useMutation({
    mutationKey: keys.createEvent(calendarId),
    mutationFn: (data) => createCalendarEvent({ calendarId, ...data }),
    onSuccess: (data) => {
      qClient.invalidateQueries({ queryKey: keys.item({ id: calendarId }) });
      qClient.invalidateQueries({ queryKey: keys.list() });
      addToast({
        type: 'short',
        method: 'success',
        description: 'Calendar:calendarEvent.toast.successfullyCreated.description',
      });
      return data;
    },
    onError: () => {
      addToast({
        type: 'short',
        method: 'error',
        description: 'Calendar:calendar.toast.actionFailed.description',
      });
    },
  });
};

export const useUpdateCalendarEventMutation = (
  calendarId: string,
): UseMutationResult<CalendarEvent, CommonError, CalendarEvent> => {
  const qClient = useQueryClient();

  return useMutation({
    mutationKey: keys.updateEvent(calendarId),
    mutationFn: (data) => updateCalendarEvent({ calendarId, ...data }),
    onSuccess: (data) => {
      qClient.invalidateQueries({ queryKey: keys.item({ id: calendarId }) });
      qClient.invalidateQueries({ queryKey: keys.list() });
      addToast({
        type: 'short',
        method: 'success',
        description: 'Calendar:calendar.toast.successfullyUpdated.description',
      });
      return data;
    },
    onError: () => {
      addToast({
        type: 'short',
        method: 'error',
        description: 'Calendar:calendar.toast.actionFailed.description',
      });
    },
  });
};

export const useDeleteCalendarEventMutation = (
  calendarId: string,
  eventId: string,
): UseMutationResult<void, CommonError, void> => {
  const qClient = useQueryClient();

  return useMutation({
    mutationKey: keys.deleteEvent(eventId),
    mutationFn: deleteCalendarEvent.bind(null, calendarId, eventId),
    onSuccess: () => {
      qClient.invalidateQueries({ queryKey: keys.item({ id: calendarId }) });
      qClient.invalidateQueries({ queryKey: keys.list() });
      addToast({
        type: 'short',
        method: 'success',
        description: 'Calendar:calendarEvent.toast.successfullyDeleted.description',
      });
    },
    onError: () => {
      addToast({
        type: 'short',
        method: 'error',
        description: 'Calendar:calendar.toast.actionFailed.description',
      });
    },
  });
};

export const useCreateRegionalCalendarMutation = (
  countryId: string,
): UseMutationResult<CalendarDTO, CommonError, CreateRegionalCalendarRequest> => {
  const qClient = useQueryClient();

  return useMutation({
    mutationKey: keys.createRegionalCalendar(countryId),
    mutationFn: createRegionalCalendar,
    onSuccess: (data) => {
      qClient.invalidateQueries({ queryKey: keys.list() });

      addToast({
        type: 'short',
        method: 'success',
        description: 'Calendar:calendar.toast.successfullyCreated.description',
      });

      return data;
    },
    onError: () => {
      addToast({
        type: 'short',
        method: 'error',
        description: 'Calendar:calendar.toast.actionFailed.description',
      });
    },
  });
};

export const useUpdateRegionalCalendarMutation = (
  calendarId: string,
): UseMutationResult<CalendarDTO, CommonError, UpdateRegionalCalendarRequest> => {
  const qClient = useQueryClient();

  return useMutation({
    mutationKey: keys.updateRegionalCalendar(calendarId),
    mutationFn: ({ id, name }) => updateRegionalCalendar(id, name),
    onSuccess: (data) => {
      qClient.invalidateQueries({ queryKey: keys.list() });
      qClient.invalidateQueries({ queryKey: keys.item({ id: data.id }) });

      addToast({
        type: 'short',
        method: 'success',
        description: 'Calendar:calendar.toast.successfullyUpdated.description',
      });
      return data;
    },
    onError: () => {
      addToast({
        type: 'short',
        method: 'error',
        description: 'Calendar:calendar.toast.actionFailed.description',
      });
    },
  });
};

export const useDeleteRegionalCalendarMutation = (calendarId: string): UseMutationResult<void, CommonError, string> => {
  const navigate = useNavigate();

  const qClient = useQueryClient();

  return useMutation({
    mutationKey: keys.deleteRegionalCalendar(calendarId),
    mutationFn: deleteRegionalCalendar.bind(null, calendarId),
    onSuccess: () => {
      qClient.invalidateQueries({ queryKey: keys.list() });

      addToast({
        type: 'short',
        method: 'success',
        description: 'Calendar:calendar.toast.successfullyDeleted.description',
      });

      navigate(pathKeys.calendars.root());
    },
    onError: () => {
      addToast({
        type: 'short',
        method: 'error',
        description: 'Calendar:calendar.toast.actionFailed.description',
      });
    },
  });
};
